import apiService from './api';
import type { NumericValue } from '@/utils/numeric';

/**
 * Winter-chill accumulation service — wraps /api/chill-units.
 *
 * Follows the weather.ts convention of unwrapping the ApiResponse envelope and
 * returning the payload (or null on failure) so pages stay terse.
 */

export type ChillModel = 'dynamic' | 'utah' | 'chilling';

export interface ChillDay {
  local_date: string;
  chill_portions_day: NumericValue;
  utah_units_day: NumericValue;
  chilling_hours_day: NumericValue;
  chill_portions_cum: NumericValue;
  utah_units_cum: NumericValue;
  chilling_hours_cum: NumericValue;
  hours_total: number;
  hours_station: number;
  hours_modelled: number;
  source: 'station' | 'open_meteo' | 'mixed';
}

export interface ChillMeta {
  season_year: number;
  season_start: string;
  season_end: string;
  through_date: string;
  timezone: string;
  source: 'station' | 'open_meteo';
  station_id: number | null;
  station_name: string | null;
  farm_id: number | null;
  farm_name: string | null;
}

export interface ChillSummary {
  chill_portions: number;
  utah_units: number;
  chilling_hours: number;
  days_modelled: number;
  days_total: number;
  last_date: string | null;
}

export interface ChillSeries {
  meta: ChillMeta;
  summary: ChillSummary;
  series: ChillDay[];
  cached: boolean;
}

export interface ChillOverviewItem {
  farm_id: number;
  farm_name: string;
  source: 'station' | 'open_meteo' | 'unavailable' | 'error';
  station_id: number | null;
  station_name: string | null;
  summary: ChillSummary | null;
}

export interface ChillOverview {
  season_year: number;
  items: ChillOverviewItem[];
}

interface FetchOpts {
  season?: number;
  refresh?: boolean;
}

function buildQuery(opts?: FetchOpts): string {
  const p = new URLSearchParams();
  if (opts?.season) p.set('season', String(opts.season));
  if (opts?.refresh) p.set('refresh', '1');
  const q = p.toString();
  return q ? `?${q}` : '';
}

class ChillUnitService {
  async getStation(id: number, opts?: FetchOpts): Promise<ChillSeries | null> {
    const res = await apiService.get<ChillSeries>(`/chill-units/stations/${id}${buildQuery(opts)}`);
    return res?.success && res.data ? res.data : null;
  }

  async getFarm(id: number, opts?: FetchOpts): Promise<ChillSeries | null> {
    const res = await apiService.get<ChillSeries>(`/chill-units/farms/${id}${buildQuery(opts)}`);
    return res?.success && res.data ? res.data : null;
  }

  async getOverview(season?: number): Promise<ChillOverview | null> {
    const q = season ? `?season=${season}` : '';
    const res = await apiService.get<ChillOverview>(`/chill-units/overview${q}`);
    return res?.success && res.data ? res.data : null;
  }
}

export const chillUnitService = new ChillUnitService();
export default chillUnitService;
